// src/types/finance.types.ts
import { Timestamp } from 'firebase/firestore';

// ============================================
// Company Settings & Configuration
// ============================================

export interface CompanySettings {
  id: string;
  companyName: string;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite?: string;
  taxId?: string; // EIN or Tax ID

  // Wire/Bank Details for Invoices
  bankName: string;
  bankAccountName: string;
  bankAccountNumber: string;
  bankRoutingNumber: string;
  bankSwiftCode?: string; // For international transfers
  bankAddress?: string;

  // Invoice Settings
  invoicePrefix: string; // e.g., "INV-"
  nextInvoiceNumber: number;
  defaultPaymentTerms: number; // Days until due (e.g., 30)
  invoiceNotes?: string; // Default notes on invoices
  invoiceFooter?: string; // Footer text for invoices

  // Default Billing Rates (used as defaults for new projects)
  defaultBillRate?: number; // Default rate charged to clients per valid lead
  defaultPayRate?: number; // Default rate paid to field staff per valid lead

  // Expense Categories
  expenseCategories: ExpenseCategory[];

  updatedAt: Timestamp;
  updatedBy: string;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export type ExpensePaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'check' | 'other';
export type ReimbursementStatus = 'owed' | 'reimbursed' | 'n/a';

// Manual income (non-invoice revenue: consulting, grants, misc). Provider-only.
export type IncomeStatus = 'received' | 'expected';

export interface Income {
  id: string;
  date: Timestamp;
  amount: number;
  category: string;            // e.g. Consulting, Grant, Reimbursement, Other
  description: string;
  status: IncomeStatus;        // received counts toward revenue; expected does not
  paymentMethod?: ExpensePaymentMethod;
  projectId?: string;
  projectName?: string;
  organizationId?: string;
  clientName?: string;
  notes?: string;
  receiptUrl?: string;
  receiptFileName?: string;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

export interface CreateIncomeDto {
  date: Date;
  amount: number;
  category: string;
  description: string;
  status: IncomeStatus;
  paymentMethod?: ExpensePaymentMethod;
  projectId?: string;
  projectName?: string;
  organizationId?: string;
  clientName?: string;
  notes?: string;
}

export interface UpdateIncomeDto {
  date?: Date;
  amount?: number;
  category?: string;
  description?: string;
  status?: IncomeStatus;
  paymentMethod?: ExpensePaymentMethod;
  projectId?: string;
  projectName?: string;
  organizationId?: string;
  clientName?: string;
  notes?: string;
}

// Default expense categories
export const DEFAULT_EXPENSE_CATEGORIES: ExpenseCategory[] = [
  { id: 'travel_flight', name: 'Flights', description: 'Air travel expenses', isActive: true },
  { id: 'travel_rental', name: 'Car Rentals', description: 'Vehicle rental expenses', isActive: true },
  { id: 'travel_hotel', name: 'Hotels', description: 'Lodging expenses', isActive: true },
  { id: 'travel_fuel', name: 'Fuel', description: 'Gas and fuel expenses', isActive: true },
  { id: 'travel_parking', name: 'Parking', description: 'Parking fees', isActive: true },
  { id: 'meals', name: 'Meals', description: 'Food and dining expenses', isActive: true },
  { id: 'supplies', name: 'Supplies', description: 'Office and field supplies', isActive: true },
  { id: 'equipment', name: 'Equipment', description: 'Tools and equipment', isActive: true },
  { id: 'services', name: 'Services', description: 'Third-party services', isActive: true },
  { id: 'software', name: 'Software', description: 'Software and subscriptions', isActive: true },
  { id: 'communications', name: 'Communications', description: 'Phone, internet, etc.', isActive: true },
  { id: 'other', name: 'Other', description: 'Miscellaneous expenses', isActive: true },
];

// ============================================
// Invoicing
// ============================================

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export type ProjectBillingType = 'lead_generation' | 'survey' | 'enrollment' | 'custom';

export interface Invoice {
  id: string;
  invoiceNumber: string; // e.g., "INV-2024-0001"

  // Client/Organization Info
  organizationId: string;
  organizationName: string;
  clientName: string;
  clientEmail?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZip?: string;

  // Project Info
  projectId: string;
  projectName: string;
  billingType: ProjectBillingType;

  // Line Items
  lineItems: InvoiceLineItem[];

  // Totals
  subtotal: number;
  taxRate?: number; // Percentage (e.g., 8.5 for 8.5%)
  taxAmount: number;
  total: number;

  // Status & Dates
  status: InvoiceStatus;
  issueDate: Timestamp;
  dueDate: Timestamp;
  paidDate?: Timestamp;

  // Payment Info
  paymentTerms: number; // Days
  notes?: string;

  // Per-lead billing breakdown (invoice page-2 detail) + carry-over adjustments
  billingDetail?: InvoiceBillingDetail;

  // Approval / send audit (invoice approval-by-email)
  sentToClientAt?: Timestamp;
  sentToEmail?: string;
  approvedAt?: Timestamp;
  approvedVia?: string;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number; // quantity * unitPrice

  // Optional linking to specific metrics
  metricType?: 'lead' | 'survey' | 'enrollment' | 'hour' | 'day' | 'unit';
}

export interface CreateInvoiceDto {
  organizationId: string;
  organizationName: string;
  clientName: string;
  clientEmail?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZip?: string;
  projectId: string;
  projectName: string;
  billingType: ProjectBillingType;
  lineItems: Omit<InvoiceLineItem, 'id'>[];
  billingDetail?: InvoiceBillingDetail;
  taxRate?: number;
  paymentTerms: number;
  notes?: string;
  issueDate?: Date;
  dueDate?: Date;
}

// Per-lead billing breakdown rendered on the invoice's page-2 detail.
export interface InvoiceBillingDetail {
  leads: { parentName: string; students: number; determination: string; amount: number }[];
  adjustments: { parentName: string; kind: 'carryover' | 'credit'; students: number; amount: number; note?: string }[];
}

export interface UpdateInvoiceDto {
  clientName?: string;
  clientEmail?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientZip?: string;
  lineItems?: Omit<InvoiceLineItem, 'id'>[];
  taxRate?: number;
  paymentTerms?: number;
  notes?: string;
  status?: InvoiceStatus;
  issueDate?: Date;
  dueDate?: Date;
  paidDate?: Date;
}

// ============================================
// Expenses
// ============================================

export interface Expense {
  id: string;

  // Basic Info
  description: string;
  amount: number;
  categoryId: string;
  categoryName: string;
  date: Timestamp;

  // Project Assignment (optional)
  projectId?: string;
  projectName?: string;

  // User who incurred expense
  userId: string;
  userName: string;

  // Receipt (optional)
  receiptUrl?: string;
  receiptFileName?: string;

  // Vendor/Payee
  vendor?: string;

  // How it was paid + whether it still needs reimbursing
  paymentMethod?: ExpensePaymentMethod;
  reimbursementStatus?: ReimbursementStatus;

  // Notes
  notes?: string;

  // Source tracking (for auto-created expenses from logistics, staff payments, etc.)
  sourceType?: 'logistics' | 'staff_payment' | 'manual';
  sourceId?: string;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

export interface CreateExpenseDto {
  description: string;
  amount: number;
  categoryId: string;
  categoryName: string;
  date: Date;
  projectId?: string;
  projectName?: string;
  userId: string;
  userName: string;
  vendor?: string;
  paymentMethod?: ExpensePaymentMethod;
  reimbursementStatus?: ReimbursementStatus;
  notes?: string;
  receiptUrl?: string;
  receiptFileName?: string;
}

export interface UpdateExpenseDto {
  description?: string;
  amount?: number;
  categoryId?: string;
  categoryName?: string;
  date?: Date;
  projectId?: string;
  projectName?: string;
  vendor?: string;
  paymentMethod?: ExpensePaymentMethod;
  reimbursementStatus?: ReimbursementStatus;
  notes?: string;
  receiptUrl?: string;
  receiptFileName?: string;
}

// ============================================
// User Payments (Payments to Field Staff/Contractors)
// ============================================

export type PaymentStatus = 'pending' | 'processing' | 'paid' | 'cancelled';
// 'advance' = discretionary up-front payment that is reconciled against (subtracted
// from) the field user's total earned at final billing closure.
export type PaymentType = 'hourly' | 'per_lead' | 'per_survey' | 'per_enrollment' | 'bonus' | 'reimbursement' | 'advance' | 'other';

export interface UserPayment {
  id: string;

  // User receiving payment
  userId: string;
  userName: string;
  userEmail: string;

  // Payment Details
  amount: number;
  paymentType: PaymentType;
  description: string;

  // Project (optional)
  projectId?: string;
  projectName?: string;

  // Period (for hourly/recurring)
  periodStart?: Timestamp;
  periodEnd?: Timestamp;

  // Work Metrics (for performance-based pay)
  metrics?: {
    hoursWorked?: number;
    leadsGenerated?: number;
    surveysCompleted?: number;
    enrollmentsCompleted?: number;
  };

  // Status & Dates
  status: PaymentStatus;
  scheduledDate?: Timestamp;
  paidDate?: Timestamp;
  paidBy?: string; // User ID of who marked it paid
  paidByName?: string; // Name of who marked it paid

  // External Reference (for tracking payments made outside system)
  externalReference?: string;
  paymentMethod?: string; // e.g., "Direct Deposit", "Check", "Venmo"

  // Notes
  notes?: string;
  paymentNotes?: string; // Admin notes added when marking as paid

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

export interface CreateUserPaymentDto {
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  paymentType: PaymentType;
  description: string;
  projectId?: string;
  projectName?: string;
  periodStart?: Date;
  periodEnd?: Date;
  metrics?: {
    hoursWorked?: number;
    leadsGenerated?: number;
    surveysCompleted?: number;
    enrollmentsCompleted?: number;
  };
  scheduledDate?: Date;
  externalReference?: string;
  paymentMethod?: string;
  notes?: string;
}

export interface UpdateUserPaymentDto {
  amount?: number;
  paymentType?: PaymentType;
  description?: string;
  status?: PaymentStatus;
  scheduledDate?: Date;
  paidDate?: Date;
  externalReference?: string;
  paymentMethod?: string;
  notes?: string;
}

// ============================================
// Project Logistics
// ============================================

export interface ProjectLogistics {
  id: string;
  projectId: string;

  // Staff Assignments
  staffAssignments: StaffAssignment[];

  // Travel Plans
  travelPlans: TravelPlan[];

  // Budget
  budgetedAmount: number;
  actualAmount: number; // Calculated from expenses

  // Notes
  notes?: string;

  // Metadata
  updatedAt: Timestamp;
  updatedBy: string;
}

export interface StaffAssignment {
  id: string;
  userId: string;
  userName: string;
  role: string; // e.g., "Team Lead", "Canvasser", "Driver"
  startDate: Timestamp;
  endDate?: Timestamp;
  hourlyRate?: number;
  dailyRate?: number;
  notes?: string;
}

export interface TravelPlan {
  id: string;
  type: 'flight' | 'hotel' | 'rental' | 'other';
  description: string;
  userId?: string;
  userName?: string;
  startDate: Timestamp;
  endDate?: Timestamp;
  estimatedCost: number;
  actualCost?: number;
  confirmationNumber?: string;
  vendor?: string;
  notes?: string;
}

// ============================================
// Financial Reports & Aggregations
// ============================================

export interface ProjectFinancialSummary {
  projectId: string;
  projectName: string;
  organizationId: string;
  organizationName: string;

  // Revenue
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;

  // Expenses
  totalExpenses: number;
  expensesByCategory: { categoryId: string; categoryName: string; amount: number }[];

  // Staff Costs
  totalStaffPayments: number;

  // Net
  grossProfit: number; // totalPaid - totalExpenses - totalStaffPayments
  profitMargin: number; // grossProfit / totalPaid * 100
}

export interface AnnualFinancialSummary {
  year: number;

  // Revenue
  totalRevenue: number;
  revenueByMonth: { month: number; amount: number }[];
  revenueByOrganization: { organizationId: string; organizationName: string; amount: number }[];

  // Expenses
  totalExpenses: number;
  expensesByMonth: { month: number; amount: number }[];
  expensesByCategory: { categoryId: string; categoryName: string; amount: number }[];

  // Staff Payments
  totalStaffPayments: number;
  staffPaymentsByMonth: { month: number; amount: number }[];

  // Net
  netIncome: number; // totalRevenue - totalExpenses - totalStaffPayments
}

export interface UserPaymentSummary {
  userId: string;
  userName: string;
  userEmail: string;

  // Totals
  totalPaid: number;
  totalPending: number;

  // By Type
  byPaymentType: { type: PaymentType; amount: number }[];

  // By Project
  byProject: { projectId: string; projectName: string; amount: number }[];

  // By Month
  byMonth: { month: string; amount: number }[]; // "2024-01" format
}
